import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';
import type { PageResponse } from './inquiryService';

export interface StudentAboutMe {
  fullName: string;
}

export interface StudentProfile {
  id: string;
  userId: string;
  aboutMe: StudentAboutMe;
  /** Null until set — registration-time capture or an explicit country update */
  countryCode: string | null;
  /** Always server-derived from countryCode — never directly editable */
  currencyCode: string | null;
  /** Registration bonus balance. Null if this student never received the
   * one-time coin grant (e.g. added STUDENT via role-switching, not as
   * their first-ever role). */
  coinBalance: number | null;
  createdDate: string;
  updatedDate: string;
}

export async function fetchStudentProfile(accessToken: string): Promise<StudentProfile | null> {
  const response = await apiFetch('/api/v1/student/profile', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (response.status === 404 || response.status === 500) {
    console.warn(`GET /api/v1/student/profile returned ${response.status} — treating as no profile`);
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch student profile: ${response.status}`);
  }
  return response.json();
}

export async function updateStudentAboutMe(
  accessToken: string,
  data: { fullName: string }
): Promise<StudentProfile> {
  const response = await apiFetch('/api/v1/student/profile/about-me', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update student about me: ${response.status} - ${errorText}`);
  }
  return response.json();
}

/**
 * Currency is never sent from the client — the server always derives it from
 * countryCode via the active country/currency mapping. See ADR 067.
 */
export async function updateStudentCountry(
  accessToken: string,
  data: { countryCode: string }
): Promise<StudentProfile> {
  const response = await apiFetch('/api/v1/student/profile/country', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update student country: ${response.status} - ${errorText}`);
  }
  return response.json();
}


// --- Coin spend table (paginated, filterable) ---

export interface StudentCoinSpendRow {
  id: string;
  threadId: string | null;
  counterpartName: string | null;
  createdAt: string;
  amount: number;
  balanceAfter: number;
  reason: string;
}

export interface RegistrationGrantSummary {
  amount: number;
  createdAt: string;
}

/** Raw shape returned by the backend — `teacherName` is mapped to `counterpartName` before this leaves the service layer. */
interface StudentCoinSpendRowResponse {
  id: string;
  threadId: string | null;
  teacherName: string | null;
  createdAt: string;
  amount: number;
  balanceAfter: number;
  reason: string;
}

export interface StudentCoinSummary {
  available: number | null;
  held: number;
  heldThreadCount: number;
}

/**
 * Paginated, filterable coin-spend history for the current student.
 *
 * `dateFrom`/`dateTo` are accepted as `Date` objects and serialized to full ISO
 * instant strings here via `.toISOString()` — callers (page-level components)
 * are responsible for constructing an end-of-day `Date` for `dateTo` if they
 * want the inclusive backend `<=` to cover the whole selected day. Per ADR 085,
 * the query param is OMITTED entirely (never sent as an empty string) when the
 * caller passes `undefined`, since the backend's `Instant` binding cannot
 * parse `""`.
 */
export async function fetchStudentCoinSpends(
  accessToken: string,
  params: { page: number; size: number; name?: string; dateFrom?: Date; dateTo?: Date }
): Promise<PageResponse<StudentCoinSpendRow>> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('size', String(params.size));
  if (params.name) qs.set('name', params.name);
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom.toISOString());
  if (params.dateTo) qs.set('dateTo', params.dateTo.toISOString());

  const response = await apiFetchWithRetry(`/api/v1/student/coin-transactions?${qs}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch coin spends: ${response.status}`);
  }
  const page: PageResponse<StudentCoinSpendRowResponse> = await response.json();
  return {
    ...page,
    content: page.content.map(({ teacherName, ...rest }) => ({
      ...rest,
      counterpartName: teacherName,
    })),
  };
}

export async function fetchStudentCoinSummary(accessToken: string): Promise<StudentCoinSummary> {
  const response = await apiFetchWithRetry('/api/v1/student/coins/summary', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch coin summary: ${response.status}`);
  }
  return response.json();
}

export async function fetchStudentRegistrationGrant(accessToken: string): Promise<RegistrationGrantSummary | null> {
  const response = await apiFetchWithRetry('/api/v1/student/coin-transactions/registration-grant', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (response.status === 204) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch registration grant: ${response.status}`);
  }
  return response.json();
}
